import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, Check } from "lucide-react";
import { Logo, PebbleMark } from "../components/Logo";
import { VENUES, HUB_BRAND, WEDDING_PHOTOS } from "../data/venues";

const HOW_IT_WORKS = [
  "Every celebration is host-approved under a separate event agreement",
  "Event fees, deposit, and vendor-insurance requirements are confirmed during planning",
  "Larger events include a coordinated parking attendant",
];

export default function WeddingsHub() {
  return (
    <div className="bg-cream min-h-screen" data-testid="weddings-hub">
      <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-charcoal/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <a href={`tel:${HUB_BRAND.phone_tel}`} className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-charcoal px-3 py-2" data-testid="hub-call-link">
              <Phone size={15} aria-hidden /> {HUB_BRAND.phone_display}
            </a>
            <a href="#venues" className="inline-flex items-center bg-copper text-cream text-sm font-semibold rounded-full px-5 py-2.5 hover:bg-copperdeep transition-colors" data-testid="hub-venues-cta">
              View venues
            </a>
          </div>
        </div>
      </header>

      <section className="relative h-[70vh] min-h-[480px] overflow-hidden" aria-label="Weddings hero">
        <img src={WEDDING_PHOTOS[0].url} alt="Sunlit garden ceremony arch" className="absolute inset-0 w-full h-full object-cover" data-testid="hub-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/35 to-charcoal/20" aria-hidden />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 pb-14 w-full">
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }}
              className="text-cream/85 uppercase tracking-[0.28em] text-xs sm:text-sm font-semibold mb-3" data-testid="hub-eyebrow">
              Elite STR Hosting Co. · Canyon Lake & the Hill Country
            </motion.p>
            <h1 className="font-serif text-cream font-semibold leading-[1.02]" data-testid="hub-headline">
              <span className="mask-line">
                <motion.span className="block text-4xl sm:text-6xl" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.4, duration: 0.85, ease: [0.16, 1, 0.3, 1] }}>
                  Four Hill Country venues.
                </motion.span>
              </span>
              <span className="mask-line">
                <motion.span className="block text-4xl sm:text-6xl italic text-[#EBC9A8]" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ delay: 0.58, duration: 0.85, ease: [0.16, 1, 0.3, 1] }}>
                  One unforgettable yes.
                </motion.span>
              </span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
              className="text-cream/90 text-lg sm:text-xl mt-5 max-w-xl" data-testid="hub-subheadline">
              Weddings, milestone birthdays, and gatherings at our family of Canyon Lake estates — one team, one simple planning process.
            </motion.p>
          </div>
        </div>
      </section>

      <section id="venues" className="scroll-mt-24 max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24" aria-label="Our venues">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.24em] text-copper font-semibold mb-2">The Collection</p>
          <h2 className="font-serif text-3xl sm:text-5xl font-semibold text-charcoal">Choose your <em className="text-copperdeep">setting</em></h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="hub-venue-cards">
          {VENUES.map((v, i) => (
            <motion.div key={v.slug}
              initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="bg-white border border-charcoal/10 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow group"
              data-testid={`venue-card-${v.slug}`}>
              <div className="relative aspect-[4/3] overflow-hidden bg-charcoal">
                {v.hero ? (
                  <img src={v.hero.url} alt={v.hero.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-charcoal to-sagedeep">
                    <PebbleMark size={56} />
                  </div>
                )}
                {v.comingSoon && (
                  <span className="absolute top-4 left-4 bg-[#C9923B] text-white text-xs font-bold rounded-full px-3.5 py-1.5">Coming soon</span>
                )}
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl font-semibold text-charcoal">{v.name}</h3>
                <p className="text-copperdeep text-sm font-semibold mt-0.5">{v.location}</p>
                <p className="text-soft mt-2.5 leading-relaxed">{v.tagline}</p>
                <p className="text-xs text-soft mt-2 italic">{v.capacity}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {v.comingSoon ? (
                    <span className="text-sm font-semibold text-soft">Details soon — ask the team</span>
                  ) : (
                    <Link to={`/weddings/${v.slug}`}
                      className="inline-flex items-center gap-1.5 bg-charcoal text-cream text-sm font-semibold rounded-full px-5 py-2.5 hover:bg-copperdeep transition-colors"
                      data-testid={`venue-view-${v.slug}`}>
                      Weddings at {v.name} <ArrowRight size={14} aria-hidden />
                    </Link>
                  )}
                  {v.site && v.site.startsWith("http") && (
                    <a href={v.site} target="_blank" rel="noreferrer" className="inline-flex items-center text-sm font-semibold text-copperdeep underline underline-offset-4 px-2 py-2.5" data-testid={`venue-site-${v.slug}`}>
                      Visit site
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-creamdark border-t border-charcoal/10 py-14" aria-label="How booking works">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 grid sm:grid-cols-3 gap-4">
          {HOW_IT_WORKS.map((item, i) => (
            <div key={i} className="flex gap-3 items-start bg-white rounded-2xl border border-charcoal/10 p-5">
              <span className="mt-0.5 p-1 rounded-full bg-sage/20 shrink-0"><Check size={14} className="text-sagedeep" aria-hidden /></span>
              <p className="text-charcoal/90 leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-soft mt-8">
          Questions? Call or text <a href={`tel:${HUB_BRAND.phone_tel}`} className="font-semibold text-copperdeep underline underline-offset-2" data-testid="hub-footer-call">{HUB_BRAND.phone_display}</a>
        </p>
      </section>
    </div>
  );
}
